import { Insurance, InsuranceDocument, InsuranceNote, InsuranceStatus } from '@centecomm/portal-service';
import { addDays } from 'date-fns';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import { createSelector } from 'reselect';
import { State } from '../../Types';
import { InsuranceStateError } from './Types/InsuranceStateError';

const insuranceSlice = ({ insurance }: State) => insurance;

const stateErrors = createSelector(insuranceSlice, ({ errors }) => errors);

// Insurance
const insuranceState = createSelector(insuranceSlice, ({ insurances }) => insurances);

const occupantInsurance = (occupantId: number | string) =>
    createSelector(insuranceState, (insurances): { [insuranceId: number]: Insurance } | undefined => {
        if (Object.hasOwnProperty.call(insurances, occupantId)) return insurances[occupantId];

        return undefined;
    });

const isOccupantLoaded = (occupantId: number | string) =>
    createSelector(
        insuranceSlice,
        ({ insurances, errors }): boolean =>
            Object.hasOwnProperty.call(insurances, occupantId) ||
            errors.some(
                ({ slice, identifier }: InsuranceStateError) =>
                    slice === 'insurances' && `${identifier}` === `${occupantId}`,
            ),
    );

const insurance = (occupantId: number | string, insuranceId: number | string) =>
    createSelector(occupantInsurance(occupantId), (occupant): Insurance | undefined => {
        if (!occupant) return undefined;

        if (Object.hasOwnProperty.call(occupant, insuranceId)) return occupant[insuranceId];

        return undefined;
    });

const isItemLoaded = (occupantId: number | string, insuranceId: number | string) =>
    createSelector(
        insuranceSlice,
        ({ insurances, errors }): boolean =>
            (Object.hasOwnProperty.call(insurances, occupantId) &&
                Object.hasOwnProperty.call(insurances[occupantId], insuranceId)) ||
            errors.some(
                ({ slice, identifier }: InsuranceStateError) =>
                    slice === 'insurances' && `${identifier}` === `${insuranceId}`,
            ),
    );

const insuranceList = (occupantId: number | string) =>
    createSelector(occupantInsurance(occupantId), (insurances): Insurance[] => {
        if (!insurances) return [];

        return Object.values(insurances);
    });

const actionableInsurances = (occupantId: number | string) =>
    createSelector(insuranceList(occupantId), (insurances) => {
        const declined = insurances.filter((item) => item.status === InsuranceStatus.Declined);

        const notSubmitted = insurances.filter((item) => item.status === InsuranceStatus.NotSubmitted);

        const pending = insurances.filter((item) => item.status === InsuranceStatus.PendingApproval);

        return [...declined, ...notSubmitted, ...pending];
    });

const declinedInsurances = (occupantId: number | string) =>
    createSelector(insuranceList(occupantId), (insurances) =>
        insurances.filter((item) => item.status === InsuranceStatus.Declined),
    );

const notSubmittedInsurances = (occupantId: number | string) =>
    createSelector(insuranceList(occupantId), (insurances) =>
        insurances.filter((item) => item.status === InsuranceStatus.NotSubmitted),
    );

const pendingInsurances = (occupantId: number | string) =>
    createSelector(insuranceList(occupantId), (insurances) =>
        insurances.filter((item) => item.status === InsuranceStatus.PendingApproval),
    );

const insuranceCount = (occupantId: number | string) =>
    createSelector(insuranceList(occupantId), (list): number => list.length);

const upcomingItems = (occupantId: number | string) =>
    createSelector(insuranceList(occupantId), (list): Insurance[] =>
        list.filter(
            ({ coverageStart, status }) =>
                status === InsuranceStatus.Approved && isAfter(new Date(coverageStart), new Date(Date.now())),
        ),
    );

const activeItems = (occupantId: number | string) =>
    createSelector(insuranceList(occupantId), (list): Insurance[] =>
        list.filter(
            ({ coverageEnd, coverageStart, status }) =>
                status === InsuranceStatus.Approved &&
                isAfter(new Date(coverageEnd), addDays(new Date(Date.now()), -1)) &&
                isBefore(new Date(coverageStart), new Date(Date.now())),
        ),
    );

const staticItems = (occupantId: number | string) =>
    createSelector(insuranceList(occupantId), (list): Insurance[] =>
        list.filter(
            ({ status, coverageEnd }) =>
                status === InsuranceStatus.Approved && isBefore(new Date(coverageEnd), addDays(new Date(Date.now()), -1)),
        ),
    );

// Documents
const documentsState = createSelector(insuranceSlice, ({ documents }) => documents);

const insuranceDocuments = (insuranceId: number | string) =>
    createSelector(documentsState, (documents): InsuranceDocument[] => {
        if (Object.hasOwnProperty.call(documents, insuranceId)) return documents[insuranceId];

        return [];
    });

const areDocumentsLoaded = (insuranceId: number | string) =>
    createSelector(
        insuranceSlice,
        ({ documents, errors }): boolean =>
            Object.hasOwnProperty.call(documents, insuranceId) ||
            errors.some(
                ({ slice, identifier }: InsuranceStateError) =>
                    slice === 'documents' && `${identifier}` === `${insuranceId}`,
            ),
    );

// Notes
const notesState = createSelector(insuranceSlice, ({ notes }) => notes);

const insuranceNotes = (insuranceId: number | string) =>
    createSelector(notesState, (notes): InsuranceNote[] => {
        if (Object.hasOwnProperty.call(notes, insuranceId)) return notes[insuranceId];

        return [];
    });

const areNotesLoaded = (insuranceId: number | string) =>
    createSelector(
        insuranceSlice,
        ({ notes, errors }): boolean =>
            Object.hasOwnProperty.call(notes, insuranceId) ||
            errors.some(
                ({ slice, identifier }: InsuranceStateError) => slice === 'notes' && `${identifier}` === `${insuranceId}`,
            ),
    );

// Pending Uploads
const pendingUploadsState = createSelector(
    insuranceSlice,
    ({ pendingDocuments }): Record<number, { occupantId: number; files: File[] }> => pendingDocuments,
);

const pendingDocumentsToUpload = createSelector(
    pendingUploadsState,
    (pendingDocuments): { insuranceId: number; files: File[]; occupantId: number }[] =>
        Object.keys(pendingDocuments).reduce(
            (agg, curr) => [
                ...agg,
                {
                    insuranceId: +curr,
                    files: pendingDocuments[curr].files,
                    occupantId: pendingDocuments[curr].occupantId,
                },
            ],
            [],
        ),
);

const pendingUploads = (insuranceId: number | string) =>
    createSelector(pendingUploadsState, (pending): File[] | undefined => {
        if (Object.hasOwnProperty.call(pending, insuranceId)) return pending[insuranceId].files;

        return undefined;
    });

const pendingUploadCount = (insuranceId: number | string) =>
    createSelector(pendingUploads(insuranceId), (docs) => (docs ? docs.length : 0));

const hasPendingUploads = (insuranceId: number | string) =>
    createSelector(
        pendingUploadsState,
        (pendingDocuments): boolean =>
            Object.hasOwnProperty.call(pendingDocuments, insuranceId) &&
            pendingDocuments[insuranceId] &&
            pendingDocuments[insuranceId].files &&
            pendingDocuments[insuranceId].files.length > 0,
    );

export default {
    actionableInsurances,
    activeItems,
    areDocumentsLoaded,
    areNotesLoaded,
    declinedInsurances,
    hasPendingUploads,
    insurance,
    insuranceCount,
    insuranceDocuments,
    insuranceList,
    insuranceNotes,
    isItemLoaded,
    isOccupantLoaded,
    notSubmittedInsurances,
    occupantInsurance,
    pendingDocumentsToUpload,
    pendingInsurances,
    pendingUploads,
    pendingUploadCount,
    stateErrors,
    staticItems,
    upcomingItems,
};
